import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import api, { HYPERLIQUID_MONITOR } from '../../infrastructure/config/api';

const DEFAULT_WALLETS = [
  '0xecb63caa47c7c4e77f60f1ce858cf28dc2b82b00',
  '0x8af700ba841f30e0a3fcb0ee4c4a9d223e1efa05',
  '0xfc667adba8d4837586078f4fdcdc29804337ca06',
  '0x45d26f28196d226497130c4bac709d808fed4029',
  '0x7c930969fcf3e5a5c78bcf2e1cefda3f53e3c8fd',
  '0x615a2071e01b28093e65d81be32cea6e173d69d5',
];
const DEFAULT_WALLET = DEFAULT_WALLETS[0];

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const number = Number(value);
  return Number.isFinite(number)
    ? number.toLocaleString('en-US', { maximumFractionDigits: 8 })
    : '—';
};

const formatRoe = (value) => {
  const ratio = Number(value);
  if (!Number.isFinite(ratio)) return '—';
  const percent = ratio * 100;
  return `${percent >= 0 ? '+' : ''}${percent.toFixed(2)}%`;
};

const formatLeverage = (value) => {
  const leverage = Number(value?.value);
  if (!Number.isFinite(leverage) || leverage <= 0) return '—';
  return `x${leverage}`;
};

const formatTime = (value) => {
  if (!value) return '—';
  const timestamp = typeof value === 'number' || /^\d+$/.test(String(value)) ? Number(value) : value;
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const formatDate = (value) => {
  const date = new Date(Number(value));
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
};

const shortWallet = (value) => {
  const wallet = String(value || '');
  return wallet.length > 12 ? `${wallet.slice(0, 6)}…${wallet.slice(-4)}` : wallet;
};

const normalizeWallets = (values) => values.map((value) => value.trim().toLowerCase()).filter(Boolean);

export default function HyperliquidWalletMonitor() {
  const [monitors, setMonitors] = useState([]);
  const [events, setEvents] = useState([]);
  const [walletAddresses, setWalletAddresses] = useState(DEFAULT_WALLETS);
  const [selectedWallet, setSelectedWallet] = useState(DEFAULT_WALLET);
  const [enabled, setEnabled] = useState(true);
  const [notifyTelegram, setNotifyTelegram] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  const load = useCallback(async ({ hydrateForm = false, walletAddress: walletOverride } = {}) => {
    try {
      const statusResponse = await api.get(HYPERLIQUID_MONITOR.STATUS);
      const nextMonitors = statusResponse.data.monitors || [statusResponse.data.monitor].filter(Boolean);
      const candidateWallet = walletOverride || selectedWallet;
      const walletForEvents = candidateWallet && nextMonitors.some((monitor) => monitor.walletAddress === candidateWallet)
        ? candidateWallet
        : nextMonitors[0]?.walletAddress || DEFAULT_WALLET;
      const eventsResponse = await api.get(HYPERLIQUID_MONITOR.EVENTS, {
        params: {
          limit: 100,
          walletAddress: walletForEvents,
        },
      });
      setMonitors(nextMonitors);
      setEvents(eventsResponse.data.events || []);
      setSelectedWallet((current) => (
        nextMonitors.some((monitor) => monitor.walletAddress === current)
          ? current
          : nextMonitors[0]?.walletAddress || DEFAULT_WALLET
      ));
      if (hydrateForm) {
        const savedWallets = nextMonitors.filter((monitor) => monitor.id).map((monitor) => monitor.walletAddress);
        setWalletAddresses(savedWallets.length > 0 ? savedWallets : DEFAULT_WALLETS);
        setEnabled(nextMonitors.some((monitor) => monitor.enabled));
        setNotifyTelegram(nextMonitors.some((monitor) => monitor.notifyTelegram));
      }
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Không thể tải trạng thái theo dõi.');
    } finally {
      setLoading(false);
    }
  }, [selectedWallet]);

  useEffect(() => {
    load({ hydrateForm: true, walletAddress: selectedWallet });
    const timer = setInterval(() => load(), 10000);
    return () => clearInterval(timer);
  }, [load, selectedWallet]);

  const updateWallet = (index, value) => {
    setWalletAddresses((current) => current.map((wallet, itemIndex) => (
      itemIndex === index ? value : wallet
    )));
  };

  const addWallet = () => {
    setWalletAddresses((current) => (current.length < 6 ? [...current, ''] : current));
  };

  const removeWallet = (index) => {
    setWalletAddresses((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const save = async () => {
    setSaving(true);
    try {
      const normalizedWallets = normalizeWallets(walletAddresses);
      const response = await api.put(HYPERLIQUID_MONITOR.CONFIG, {
        walletAddresses: normalizedWallets,
        enabled,
        notifyTelegram,
      });
      setMonitors(response.data.monitors || []);
      let syncMessage = '';
      if (enabled) {
        setSyncing(true);
        try {
          await api.post(HYPERLIQUID_MONITOR.SYNC);
        } catch (syncError) {
          syncMessage = syncError.response?.data?.message || 'Đồng bộ sau khi lưu thất bại.';
        }
      }
      await load({ hydrateForm: true });
      enqueueSnackbar(enabled
        ? `Đã lưu cấu hình ${normalizedWallets.length} ví Hyperliquid.${syncMessage ? ` ${syncMessage}` : ''}`
        : 'Đã lưu và tắt theo dõi.',
      { variant: syncMessage ? 'warning' : 'success' });
    } catch (requestError) {
      enqueueSnackbar(requestError.response?.data?.message || 'Không thể lưu hoặc đồng bộ cấu hình.', { variant: 'error' });
    } finally {
      setSyncing(false);
      setSaving(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const response = await api.post(HYPERLIQUID_MONITOR.SYNC);
      await load();
      enqueueSnackbar(`Đã đồng bộ ${response.data.syncedWallets || monitors.length} ví.`, { variant: 'success' });
    } catch (requestError) {
      enqueueSnackbar(requestError.response?.data?.message || 'Đồng bộ thất bại.', { variant: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;
  }

  const savedWallets = normalizeWallets(monitors.filter((monitor) => monitor.id).map((monitor) => monitor.walletAddress));
  const formWallets = normalizeWallets(walletAddresses);
  const hasUnsavedChanges = (
    savedWallets.join(',') !== formWallets.join(',') ||
    monitors.some((monitor) => monitor.id) && (
      enabled !== monitors.some((monitor) => monitor.enabled) ||
      notifyTelegram !== monitors.some((monitor) => monitor.notifyTelegram)
    )
  );
  const selectedMonitor = monitors.find((monitor) => monitor.walletAddress === selectedWallet);
  const positions = selectedMonitor ? Object.values(selectedMonitor.positions || {}).map((position) => ({
    ...position,
    walletAddress: selectedMonitor.walletAddress,
    monitorId: selectedMonitor.id,
  })) : [];
  const selectedEvents = events.filter((event) => (
    !selectedWallet || String(event.wallet_address || event.walletAddress || '').toLowerCase() === selectedWallet
  ));

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>Hyperliquid Wallet Monitor</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Theo dõi vị thế perpetual và giao dịch của tối đa 6 ví. Chọn một wallet để xem dữ liệu riêng, hệ thống vẫn theo dõi tất cả ở nền.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {monitors.every((monitor) => monitor.status === 'DISABLED') && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Bật “Theo dõi”, sau đó bấm “Lưu & đồng bộ” để tải positions hiện tại.
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack spacing={2}>
          {walletAddresses.map((walletAddress, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                label={`Wallet address ${index + 1}`}
                value={walletAddress}
                onChange={(event) => updateWallet(index, event.target.value)}
                fullWidth
                helperText={index === 0 ? `Bộ mặc định đang nạp sẵn 6 ví, ví chính: ${DEFAULT_WALLET}` : undefined}
              />
              {walletAddresses.length > 1 && (
                <Button color="error" onClick={() => removeWallet(index)}>Xóa</Button>
              )}
            </Box>
          ))}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button variant="outlined" onClick={addWallet} disabled={walletAddresses.length >= 6}>
              + Thêm wallet ({walletAddresses.length}/6)
            </Button>
            <FormControlLabel
              control={<Switch checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />}
              label="Theo dõi"
            />
            <FormControlLabel
              control={<Switch checked={notifyTelegram} onChange={(event) => setNotifyTelegram(event.target.checked)} />}
              label="Telegram (kênh hệ thống)"
            />
            <Button variant="contained" onClick={save} disabled={saving || syncing || formWallets.length === 0}>
              {saving || syncing ? <CircularProgress size={22} /> : 'Lưu & đồng bộ'}
            </Button>
            <Button
              variant="outlined"
              onClick={sync}
              disabled={syncing || saving || !enabled || hasUnsavedChanges || savedWallets.length === 0}
            >
              {syncing ? <CircularProgress size={22} /> : 'Đồng bộ ngay'}
            </Button>
          </Box>
        </Stack>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
          {monitors.filter((monitor) => monitor.id).map((monitor) => (
            <Chip
              key={monitor.id}
              label={`${shortWallet(monitor.walletAddress)}: ${monitor.status} · ${formatTime(monitor.lastSyncedAt)}`}
              color={monitor.status === 'CONNECTED' ? 'success' : monitor.status === 'ERROR' ? 'error' : 'default'}
            />
          ))}
        </Box>
      </Paper>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="wallet-view-select-label">Wallet đang xem</InputLabel>
        <Select
          labelId="wallet-view-select-label"
          value={selectedWallet}
          label="Wallet đang xem"
          onChange={(event) => setSelectedWallet(event.target.value)}
        >
          {monitors.map((monitor) => (
            <MenuItem key={monitor.id || monitor.walletAddress} value={monitor.walletAddress}>
              {monitor.walletAddress} · {Object.keys(monitor.positions || {}).length} positions · {monitor.status}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography variant="h5" gutterBottom>
        Positions đang mở · {shortWallet(selectedWallet)}
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Wallet</TableCell><TableCell>Coin</TableCell><TableCell>Side</TableCell>
            <TableCell align="right">Đòn bẩy</TableCell><TableCell align="right">Entry</TableCell>
            <TableCell align="right">ROE</TableCell><TableCell>Mở lúc</TableCell>
            <TableCell align="right">Liquidation</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {positions.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center">Không có position đang mở.</TableCell></TableRow>
            ) : positions.map((position) => (
              <TableRow key={`${position.monitorId}-${position.coin}`}>
                <TableCell title={position.walletAddress}>{shortWallet(position.walletAddress)}</TableCell>
                <TableCell>{position.coin}</TableCell>
                <TableCell><Chip size="small" label={position.side} color={position.side === 'LONG' ? 'success' : 'error'} /></TableCell>
                <TableCell align="right">{formatLeverage(position.leverage)}</TableCell>
                <TableCell align="right">{formatNumber(position.entryPrice)}</TableCell>
                <TableCell align="right">
                  <Box sx={{ color: Number(position.returnOnEquity) >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>
                    {formatRoe(position.returnOnEquity)}
                  </Box>
                </TableCell>
                <TableCell>
                  {position.openedAt
                    ? formatTime(position.openedAt)
                    : position.openedBefore
                      ? `Trước ${formatDate(position.openedBefore)}`
                      : position.openTimeCheckedAt ? 'Không xác định' : 'Đang tra cứu…'}
                </TableCell>
                <TableCell align="right">{formatNumber(position.liquidationPrice)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h5" gutterBottom>
        Transactions / events · {shortWallet(selectedWallet)}
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Thời gian</TableCell><TableCell>Wallet</TableCell><TableCell>Event</TableCell>
            <TableCell>Coin</TableCell><TableCell align="right">Size</TableCell>
            <TableCell align="right">Price</TableCell><TableCell>Thông báo</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {selectedEvents.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center">Chưa có transaction trong khoảng backfill.</TableCell></TableRow>
            ) : selectedEvents.map((event) => (
              <TableRow key={event.id}>
                <TableCell>{formatTime(event.fill_time)}</TableCell>
                <TableCell title={event.wallet_address}>{shortWallet(event.wallet_address)}</TableCell>
                <TableCell>{event.event_type}</TableCell>
                <TableCell>{event.coin}</TableCell>
                <TableCell align="right">{formatNumber(event.size)}</TableCell>
                <TableCell align="right">{formatNumber(event.price)}</TableCell>
                <TableCell>{event.notification_status}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
}
